import { createStore } from 'vuex'
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    updateProfile,
    signOut,
    onAuthStateChanged
} from 'firebase/auth'
import { auth } from '@/firebase'
import apiService from '@/services/apiService'

export default createStore({
    state: {
        user: null,
        token: localStorage.getItem('token') || '',
        loginError: null,
        registerError: null,
        registerSuccess: false,
        loading: false,
        fetchingUser: false,
        authInitialized: false
    },
    mutations: {
        setUser(state, user) {
            state.user = user
        },
        setToken(state, token) {
            state.token = token
            if (token) {
                localStorage.setItem('token', token)
            } else {
                localStorage.removeItem('token')
            }
        },
        clearAuth(state) {
            state.user = null
            state.token = ''
            state.fetchingUser = false
            localStorage.removeItem('token')
        },
        setLoginError(state, error) {
            state.loginError = error
        },
        setRegisterError(state, error) {
            state.registerError = error
        },
        setRegisterSuccess(state, success) {
            state.registerSuccess = success
        },
        setLoading(state, loading) {
            state.loading = loading
        },
        setFetchingUser(state, fetchingUser) {
            state.fetchingUser = fetchingUser
        },
        setAuthInitialized(state, initialized) {
            state.authInitialized = initialized
        }
    },
    actions: {
        initAuth({ commit, dispatch }) {
            return new Promise((resolve) => {
                if (!auth) {
                    commit('setAuthInitialized', true)
                    resolve()
                    return
                }
                onAuthStateChanged(auth, async (firebaseUser) => {
                    if (firebaseUser) {
                        const token = await firebaseUser.getIdToken()
                        commit('setToken', token)
                        await dispatch('fetchCurrentUser')
                    } else {
                        commit('clearAuth')
                    }
                    commit('setAuthInitialized', true)
                    resolve()
                })
            })
        },

        async login({ commit, dispatch }, credentials) {
            commit('setLoading', true)
            commit('setLoginError', null)
            try {
                const userCredential = await signInWithEmailAndPassword(
                    auth,
                    credentials.email,
                    credentials.password
                )
                if (!userCredential.user.emailVerified) {
                    await signOut(auth)
                    commit('setLoginError', 'Veuillez vérifier votre email avant de vous connecter.')
                    return false
                }
                const token = await userCredential.user.getIdToken()
                commit('setToken', token)
                await dispatch('fetchCurrentUser')
                return true
            } catch (error) {
                console.error('Login failed:', error)
                const errorMessage = getFirebaseErrorMessage(error.code)
                commit('setLoginError', errorMessage)
                return false
            } finally {
                commit('setLoading', false)
            }
        },

        async register({ commit }, userData) {
            commit('setLoading', true)
            commit('setRegisterError', null)
            commit('setRegisterSuccess', false)
            try {
                const userCredential = await createUserWithEmailAndPassword(
                    auth,
                    userData.email,
                    userData.password
                )
                if (userData.name) {
                    await updateProfile(userCredential.user, { displayName: userData.name })
                }
                await sendEmailVerification(userCredential.user, {
                    url: window.location.origin + '/verify-email',
                    handleCodeInApp: true
                })
                await signOut(auth)
                commit('setRegisterSuccess', true)
                return true
            } catch (error) {
                console.error('Registration failed:', error)
                const errorMessage = getFirebaseErrorMessage(error.code)
                commit('setRegisterError', errorMessage)
                return false
            } finally {
                commit('setLoading', false)
            }
        },

        async fetchCurrentUser({ commit, state }) {
            if (!state.token) return

            if (state.fetchingUser) {
                console.log('fetchCurrentUser already in progress, skipping...')
                return
            }

            if (state.user) {
                console.log('User already exists in state, skipping fetch...')
                return
            }

            commit('setFetchingUser', true)
            try {
                const user = await apiService.getCurrentUser()
                commit('setUser', user)
            } catch (error) {
                console.error('Failed to fetch user:', error)
            } finally {
                commit('setFetchingUser', false)
            }
        },

        async updateUserProfile({ commit, state }, userData) {
            commit('setLoading', true)
            try {
                const response = await apiService.updateUserProfile(userData)
                const updatedUser = { ...state.user, ...response }
                commit('setUser', updatedUser)
                return true
            } catch (error) {
                console.error('Profile update failed:', error)
                throw error
            } finally {
                commit('setLoading', false)
            }
        },

        async logout({ commit }) {
            try {
                await signOut(auth)
            } catch (error) {
                console.error('Logout error:', error)
            } finally {
                commit('clearAuth')
            }
        },

        async refreshToken({ commit }) {
            const currentUser = auth.currentUser
            if (currentUser) {
                const token = await currentUser.getIdToken(true)
                commit('setToken', token)
                return token
            }
            return null
        }
    },
    getters: {
        isAuthenticated: state => !!state.token,
        user: state => state.user,
        token: state => state.token,
        loginError: state => state.loginError,
        registerError: state => state.registerError,
        registerSuccess: state => state.registerSuccess,
        loading: state => state.loading,
        authInitialized: state => state.authInitialized
    }
})

function getFirebaseErrorMessage(errorCode) {
    const errorMessages = {
        'auth/invalid-email': 'Adresse email invalide.',
        'auth/user-disabled': 'Ce compte a été désactivé.',
        'auth/user-not-found': 'Aucun compte trouvé avec cet email.',
        'auth/wrong-password': 'Mot de passe incorrect.',
        'auth/invalid-credential': 'Email ou mot de passe incorrect.',
        'auth/email-already-in-use': 'Un compte existe déjà avec cet email.',
        'auth/weak-password': 'Le mot de passe doit contenir au moins 6 caractères.',
        'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard.',
        'auth/network-request-failed': 'Erreur réseau. Vérifiez votre connexion.'
    }
    return errorMessages[errorCode] || 'Une erreur est survenue. Réessayez.'
}